import axios from 'axios'

import { useSessionStore } from '@/shared/store/session'

/**
 * Cliente HTTP único de la aplicación.
 *
 * Dos decisiones de seguridad que conviene no revertir:
 *  1. El access token vive SOLO en memoria (store de Zustand). Nada de
 *     localStorage: cualquier XSS lo leería.
 *  2. El refresh token viaja en una cookie HttpOnly que el JS no puede leer;
 *     por eso `withCredentials: true` y por eso el refresh no lleva cuerpo.
 */
const BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1'

// La cookie de refresh es SameSite=Strict. Si la API vive en otro sitio que la
// página —y para el navegador `localhost` y `127.0.0.1` son sitios distintos—
// el navegador la descarta sin decir nada y la sesión se pierde en cada
// recarga. Es un fallo silencioso caro de diagnosticar: mejor gritarlo.
if (import.meta.env.DEV && /^https?:\/\//.test(BASE_URL)) {
  const apiHost = new URL(BASE_URL).hostname
  if (apiHost !== window.location.hostname) {
    console.warn(
      `[humania] VITE_API_BASE_URL apunta a "${apiHost}" y la página se sirve desde ` +
        `"${window.location.hostname}". La cookie de sesión no sobrevivirá a una ` +
        'recarga. Deja VITE_API_BASE_URL vacío para usar el proxy de Vite.'
    )
  }
}

export const api = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
})

/**
 * Compone una ruta que devuelve la API —el logo de una empresa, por ejemplo—
 * con la base del cliente.
 *
 * La API las entrega relativas a su propia raíz porque no sabe desde dónde se
 * la consume: con `VITE_API_BASE_URL` apuntando a otro host, un `<img src>`
 * con la ruta pelada se resolvería contra el origen de la página y daría 404.
 */
export function apiAssetUrl(path) {
  if (!path) return null
  if (/^https?:\/\//.test(path)) return path
  return `${BASE_URL}${path}`
}

/** Genera el X-Request-Id que correlaciona el log del navegador con el del backend. */
const newRequestId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`

api.interceptors.request.use((config) => {
  const { accessToken } = useSessionStore.getState()
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`
  }
  config.headers['X-Request-Id'] = config.headers['X-Request-Id'] || newRequestId()
  return config
})

// --- Refresh con cola -------------------------------------------------------
// Si caducan 5 peticiones a la vez, solo una llama a /auth/refresh; el resto
// espera ese resultado. Sin esto se dispararían 5 rotaciones de refresh token
// y el backend invalidaría la familia entera por sospecha de reuso.
let refreshPromise = null

const refreshSession = () => {
  if (!refreshPromise) {
    refreshPromise = api
      .post('/auth/refresh', null, { skipAuthRefresh: true })
      .then(({ data }) => {
        useSessionStore.getState().setSession(data)
        return data.access_token
      })
      .finally(() => {
        refreshPromise = null
      })
  }
  return refreshPromise
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error

    if (!response || !config || config.skipAuthRefresh || config._retried) {
      return Promise.reject(normalizeError(error))
    }

    if (response.status === 401) {
      try {
        const token = await refreshSession()
        config._retried = true
        config.headers.Authorization = `Bearer ${token}`
        return api.request(config)
      } catch {
        useSessionStore.getState().clearSession()
      }
    }

    return Promise.reject(normalizeError(error))
  }
)

/**
 * Traduce la respuesta RFC 7807 del backend a un error manejable en la UI.
 * `requestId` es lo que el usuario le pasa a soporte cuando algo falla.
 */
export function normalizeError(error) {
  const problem = error.response?.data
  // Si el backend respondió pero el cuerpo no es problem+json (p. ej. un 502 del
  // balanceador), se conserva el status en el código en vez de llamarlo "red".
  const fallbackCode = error.response
    ? `http_${error.response.status}`
    : error.code === 'ECONNABORTED'
      ? 'timeout'
      : 'network_error'

  return {
    status: error.response?.status ?? 0,
    code: problem?.code ?? fallbackCode,
    title: problem?.title ?? 'No se pudo completar la operación',
    detail: problem?.detail ?? error.message,
    requestId: problem?.request_id ?? error.response?.headers?.['x-request-id'],
    fieldErrors: problem?.errors ?? [],
  }
}
